<template>
  <b-container fluid
               class="p-0">
    <b-row class="filter"
           fluid
           :class="{ 'filter-sm': size === 'sm' }"
           @submit.prevent
           v-if="showFilter()">
      <b-col v-for="filter in filters"
             :key="filter.name"
             cols="auto">
        <label>{{ filter.label }}</label>

        <b-form-select v-if="filter.type === 'Code'"
                       :options=filter.options
                       v-model=filter.value
                       @change="onChange"/>

        <template v-else-if="filter.type === 'Date'">
          <div class="date-wrap">
            <!-- Start date -->
            <b-form-group>
              <b-input-group class="form-inline">
                <b-form-input name="start-date"
                              type="date"
                              v-model="filter.duration[0]"
                              class="custom-input"
                              @change="onChange"
                              placeholder="YYYY-MM-DD"/>
              </b-input-group>
            </b-form-group>
            <!-- /Start date -->

            <span class="tilde">&#126;</span>

            <!-- End date -->
            <b-form-group>
              <b-input-group class="form-inline">
                <b-form-input name="end-date"
                              type="date"
                              v-model="filter.duration[1]"
                              @change="onChange"
                              class="custom-input"
                              placeholder="YYYY-MM-DD"/>
              </b-input-group>
            </b-form-group>
            <!-- /End date -->
          </div>
        </template>

        <esg-base-search-box v-else-if="filter.type === 'Reference'"
                             :value="filter.displayName"
                             :table="filter.table"
                             @rowClicked="(item, modalId) => onRowClicked(item, modalId, filter)"
                             modal-size="md"/>

        <b-form-input v-else
                      :name=filter.name
                      v-model.trim=filter.value
                      @keyup.enter="onChange"/>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import EsgBaseSearchBox from "@/components/esg/base/form/EsgBaseSearchBox.vue";

export default {
  components: {EsgBaseSearchBox},
  props: ['metadata', 'loading', 'size'],
  watch: {
    metadata: {
      immediate: true,
      deep: true,
      handler() {
        this.initialize()
      }
    }
  },
  data() {
    return {
      filters: []
    }
  },
  methods: {
    async initialize() {
      this.setLoading(true);

      // If no metadata is provided, return
      if (!this.metadata) return;

      // Construct filters
      let filters = [];
      for (const col of this.metadata) {
        // If the column doesn't have to be filtered, return
        if (!col.filtered) continue;

        // Construct filter
        let filter = {
          name: col.name,
          label: col.label,
          type: col.type || 'String',
          value: null,
        };
        filter.operation = this.operation(col.type);
        filter.options = await this.options(col.categories);

        // If the type is 'Date', add duration (start and end date)
        if (filter.type === 'Date') {
          filter.duration = [null, null]
        }

        // If the type is 'Reference', add target table and reference ID field
        if (filter.type === 'Reference') {
          filter.table = col.table;
          filter.referenceId = col.referenceId;
          filter.referenceDisplayName = col.referenceDisplayName;
          filter.displayName = null;
        }

        filters.push(filter);
      }
      this.filters = filters;

      this.setLoading(false);
    },
    operation(type) {

      if (type === 'String') {
        return 'LIKE';
      } else if (type === 'Date') {
        return 'BETWEEN';
      } else {
        return 'EQUAL';
      }
    },
    async options(categories) {
      if (!categories) return null;

      // Construct filters for tenant ID
      let filters = [{
        name: 'esg_base_common_code.tenant_id',
        operation: 'EQUAL',
        value: this.tenantId()
      }];

      // and categories
      categories.forEach((cat, index) => {
        filters.push({
          name: `esg_base_common_code.category_${index + 1}`,
          operation: 'EQUAL',
          value: cat
        })
      });

      // Fetch code info
      let response = await axios.get(`/esg/lca/tables/esg_base_common_code`, {
        headers: {'Authorization': accessToken()},
        params: {filters}
      });

      // Construct options using the code info
      let options = [{value: null, text: 'All'}]
      let codes = response.data.list;

      // Sort by 'sequence' field
      codes.sort((a, b) => a.sequence - b.sequence);

      if (codes) {
        codes.forEach(code => {
          options.push({value: code.id, text: code.label})
        });
      }
      return options;
    },
    showFilter() {
      return this.metadata && this.metadata.some(col => col.filtered === true);
    },
    onChange() {
      this.$emit('update:filters', this.filters)
    },
    onRowClicked(item, modalId, filter) {
      filter.value = item[filter.referenceId];
      filter.displayName = item[filter.referenceDisplayName];
      this.$bvModal.hide(modalId);
      this.onChange();
    },
    setLoading(bool) {
      this.$emit('update:loading', bool);
    }
  }
}
</script>

<style scoped>
/* Input styles */
.custom-select {
  padding: 0.533rem 0.8rem 0.533rem 1.066rem;
  height: 2.666rem;
  border-radius: 10px;
  border: 1px solid #E7E7EE;
  background-color: #FFF;
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15.834 7.5L10.0007 13.3333L4.16732 7.5" stroke="%232E2E38" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>');
  background-position: calc(100% - 12px) 9px;
  background-size: 20px 20px;
  background-repeat: no-repeat;
  font-family: 'NanumGothic';
  color: #2E2E38;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
}

.custom-select:focus {
  border-color: #E7E7EE;
  box-shadow: unset;
}

.form-control {
  padding: 0.533rem 0.8rem 0.533rem 1.066rem;
  height: 2.666rem !important;
  border-radius: 10px;
  border: 1px solid #E7E7EE;
  background-color: #FFF;
  font-family: NanumGothic, serif;
  color: #2E2E38;
  font-size: 16px;
  font-weight: 400;
  line-height: 150% !important;
  margin-bottom: 0;
  width: 12rem;
}

.form-control:focus {
  box-shadow: none;
  border-color: unset;
}

/* Filter container styles */
.filter {
  gap: .5rem 0;
}

.filter.filter-sm .form-control {
  font-size: .875rem;
  height: 2rem !important;
  width: 10rem;
}

.filter.filter-sm label {
  font-size: .875rem;
  margin-bottom: .25rem;
}

/* 날짜 영역 */
.date-wrap {
  display: flex;
  align-items: center;
  gap: 0 0.666rem;
}

.date-wrap .form-group {
  margin-bottom: 0
}

.date-wrap .tilde {
  color: #747480;
  font-family: NanumGothic, serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
}
</style>